import { triggerEvent } from './events';
import { Manifest } from '../configuration/Manifest';
import { AppRegistry } from '../models/app/AppRegistry';
import { AppsHydration } from '../models/app/AppsHydration';
import { getHydrationPoint, HYDRATION_KEY } from './getHydrationPoint';
import { IAppsResponse } from '../models/commonTypes';
import { ApplicationParameters } from '../models/ApplicationParameters';

export function getElementsForApp(): Array<HTMLElement> {
    const ksysAppTemplateIdDataKey = 'data-ksys-app-template-id';
    const ksysAppTemplateVersionDataKey = 'data-ksys-app-template-version';
    const elements = document.querySelectorAll(
        `[${ksysAppTemplateIdDataKey}="${Manifest.ksysAppTemplateId}"]`
    );
    const elementsWithCombinedVersion = document.querySelectorAll(
        `[${ksysAppTemplateIdDataKey}="${Manifest.ksysAppTemplateId}-${Manifest.version}"]`
    );
    let elementsCollection: Array<HTMLElement> = [];
    if (elements) {
        elementsCollection = Array.from(elements) as Array<HTMLElement>;
        const versions = Object.keys(
            elementsCollection.reduce(
                (acc: { [key: string]: boolean }, element) => {
                    const version = element.getAttribute(
                        ksysAppTemplateVersionDataKey
                    );
                    if (version) {
                        acc[version] = true;
                    }
                    return acc;
                },
                { [Manifest.version]: true }
            )
        );
        if (versions && versions.length > 0) {
            const highestVersion = versions.sort().reverse()[0];
            elementsCollection = elementsCollection.filter(element => {
                const version = element.getAttribute(
                    ksysAppTemplateVersionDataKey
                );
                return (
                    (!version && Manifest.version === highestVersion) ||
                    version === Manifest.version
                );
            });
        }
    }
    if (elementsWithCombinedVersion) {
        elementsCollection.push(
            ...(Array.from(elementsWithCombinedVersion) as Array<HTMLElement>)
        );
    }
    return elementsCollection;
}

export function getAppKey(appParams: ApplicationParameters) {
    const {
        configurationKey = 'default',
        ksysAppTemplateVersion: version = Manifest.version,
        applicationCode = Manifest.getApplicationGuid(),
    } = appParams;
    return `${Manifest.ksysAppTemplateId}-${version}|${configurationKey}|${
        applicationCode.length > 0 ? `|${applicationCode}` : ''
    }`;
}

function getAppRegistry(): AppRegistry {
    const key = '__ksys-app-registry__';
    if (!(window as any)[key]) {
        (window as any)[key] = {};
    }
    return (window as any)[key];
}

export function registerAppStart(appParams: ApplicationParameters) {
    const appKey = getAppKey(appParams);
    const appRegistry = getAppRegistry();
    appRegistry[appKey] = {
        appKey,
        status: 'started',
    };
}

export function registerAppLoaded(
    htmlElement: HTMLElement,
    appParams: ApplicationParameters
) {
    const appKey = getAppKey(appParams);
    const appRegistry = getAppRegistry();
    const app = appRegistry[appKey];
    if (app) {
        app.status = 'loaded';
    }
    triggerEvent(`ksys-app-loaded`, htmlElement);
    setDocDoneIfReady();
}

export function areAllAppsLoaded() {
    const appRegistry = getAppRegistry();
    let allLoaded = true;
    for (const key of Object.keys(appRegistry)) {
        const app = appRegistry[key];
        allLoaded = allLoaded && app.status === 'loaded';
        if (!allLoaded) {
            break;
        }
    }
    return allLoaded;
}

export async function setDocDoneIfReady() {
    const allLoaded = areAllAppsLoaded();
    if (allLoaded) {
        /* tslint:disable-next-line:no-string-literal */
        const hasFonts = !!(document as any)['fonts'];
        /* tslint:disable-next-line:no-string-literal */
        const fontsReady = hasFonts && (await (document as any)['fonts'].ready);
        if (!hasFonts || (fontsReady && fontsReady.status === 'loaded')) {
            setTimeout(() => {
                /* tslint:disable-next-line:no-string-literal */
                (window as any)['docdone'] = true;
                triggerEvent('ksys-apps-loaded');
            }, 1000);
        }
    }
}

export function getAppHydration(appParams: ApplicationParameters) {
    if (appParams) {
        const hydrationId = getAppKey(appParams);
        const hydrationPoint = getHydrationPoint();
        return (
            hydrationPoint &&
            hydrationPoint.appsByKey &&
            hydrationPoint.appsByKey[hydrationId]
        );
    }
}

export function getManagerHydration() {
    const hydrationPoint = getHydrationPoint();
    return hydrationPoint && hydrationPoint.manager;
}

export function setHydrationPoint(appsResponse: IAppsResponse) {
    const appsHydration: AppsHydration = {
        ...appsResponse,
        appsByKey: {
            ...pivotCollection(
                appsResponse.apps || [],
                appResponse => appResponse.app.id || 'NO_ID'
            ),
            ...pivotCollection(
                appsResponse.apps || [],
                appResponse =>
                    `${appResponse.app.id}|${appResponse.app.applicationCode}`
            ),
        },
    };
    (window as any)[HYDRATION_KEY] = appsHydration;
}

export function pivotCollection<T>(
    collection: Array<T>,
    getKeyFn: (value: T) => string,
    getValueFn: (value: T) => any = value => value
): { [key: string]: T } {
    return (collection || []).reduce((acc: { [key: string]: any }, element) => {
        const key = getKeyFn(element);
        acc[key] = getValueFn(element);
        return acc;
    }, {});
}

export function clearDocumentCache(): void {
    // Clear document-related sessionStorage keys with shareClassDetail_ prefix
    if (
        typeof sessionStorage === 'undefined' ||
        sessionStorage === null ||
        sessionStorage.length === 0
    ) {
        return;
    }

    const keysToRemove = Object.keys(sessionStorage).filter(key =>
        key.startsWith('shareClassDetail_')
    );

    // Remove the keys from sessionStorage
    if (keysToRemove.length > 0) {
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
    }
}



// WEBPACK FOOTER //
// ./src/utils/initialize.ts